import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/application/stores/auth.store'
import type { User, UserRole } from '@/domains/types'
import { ErrorState } from './ErrorState'
import { LoadingState } from './LoadingState'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

/**
 * After SSO the browser lands here with a JWT token.
 * Determine the right destination:
 *
 * 1. Privileged roles (streamer/admin/moderator) → /manage/dashboard, always.
 * 2. Viewer who is author or team member of at least one investigation
 *    → /manage/profile (they have something to manage)
 * 3. Pure viewer with no investigations → / (public homepage)
 */
async function resolveRedirect(role: UserRole, token: string): Promise<string> {
  // Rule 1 — privileged users always land in the management interface.
  if (role === 'streamer' || role === 'moderator' || role === 'admin') {
    return '/manage/dashboard'
  }

  // Rules 2 & 3 — check for managed investigations to route the viewer.
  try {
    const result = await fetch(`${API_BASE}/investigations/my`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (result.ok) {
      const data: unknown[] = await result.json()
      if (Array.isArray(data) && data.length > 0) return '/manage/profile'
    }
  } catch {
    // Ignore — fall through to public homepage
  }

  // Rule 3 — pure viewer with no investigations.
  return '/'
}

// ── Simple JWT payload decoder (no crypto verification) ───────────────────────
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const base64Str = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = atob(base64Str.padEnd(base64Str.length + ((4 - (base64Str.length % 4)) % 4), '='))
    const parsed: Record<string, unknown> = JSON.parse(json)
    return parsed
  } catch {
    return null
  }
}

function friendlyError(code: string, translate: (key: string) => string): string {
  if (code === 'no_account') return translate('auth.oauth_error_no_account')
  if (code === 'account_pending') return translate('auth.oauth_error_account_pending')
  if (code.endsWith('_denied')) return translate('auth.oauth_error_denied')
  if (code.endsWith('_failed')) return translate('auth.oauth_error_failed')
  return translate('auth.oauth_error_unknown')
}

function isUserRole(role: unknown): role is UserRole {
  return role === 'user' || role === 'streamer' || role === 'moderator' || role === 'admin'
}

/**
 * OAuthCallbackHandlerSection
 *
 * Owns the entire OAuth callback flow:
 * - Reads the `token` / `error` from the URL query string on mount.
 * - Decodes the JWT payload to derive the authenticated `User` and role.
 * - Logs the user into the auth store.
 * - Resolves the proper post-login redirect (privileged users → management
 *   dashboard; viewers honoring the saved login context) and navigates there.
 * - Renders either a loading state (while processing) or a user-friendly
 *   error state with a link back to the login page.
 */
export function OAuthCallbackHandlerSection() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function handle() {
      const params = new URLSearchParams(window.location.search)
      const token = params.get('token')
      const refreshToken = params.get('refreshToken')
      const errCode = params.get('error')

      if (errCode) {
        setError(friendlyError(errCode, t))
        return
      }

      if (!token) {
        setError(t('auth.oauth_error_unknown'))
        return
      }

      const payload = decodeJwtPayload(token)
      if (!payload) {
        setError(t('auth.oauth_error_unknown'))
        return
      }

      const timestamp = new Date().toISOString()
      const rolesRaw = payload['roles']
      const roles: string[] = Array.isArray(rolesRaw)
        ? rolesRaw.filter((item): item is string => typeof item === 'string')
        : []
      const role: UserRole = isUserRole(roles[0]) ? roles[0] : 'user'

      const subject = payload['sub']
      const uname = payload['username']
      const user: User = {
        id: typeof subject === 'string' ? subject : '',
        username: typeof uname === 'string' ? uname : '',
        displayName: typeof uname === 'string' ? uname : '',
        role,
        createdAt: timestamp,
        updatedAt: timestamp,
      }

      login(user, token, refreshToken ?? '')

      // Read and clear the context written by SocialLoginButtons before the redirect.
      const dest = await resolveRedirect(role, token)
      void navigate({ to: dest })
    }
    void handle()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <ErrorState message={error} />
  return <LoadingState />
}
